const ROUTE_TEMPLATES = [
  ['api', 'health'],
  ['api', 'observability-config'],
  ['eb-web-canary'],
  ['orders', ':id'],
  ['events', ':id'],
  ['items', ':id'],
  ['profile', ':id'],
  ['account'],
  ['manage', 'events'],
  ['signin'],
  ['conversions-manager', ':id', 'tracking'],
  ['api', 'conversions-manager', 'v1', 'tracking-beacons'],
  ['api', 'conversions-manager', 'v1', 'tracking-beacons', 'event'],
  ['api', 'conversions-manager', 'v1', 'attribution', 'click'],
  ['api', 'conversions-manager', 'v1', 'attribution', 'event'],
  ['api', 'conversions-manager', 'v1', 'attribution', 'access-creds'],
  ['api', 'conversions-manager', 'v1', 'attribution', 'seeka-creds'],
  ['api', 'conversions-manager', 'v1', 'attribution', 'pending-click'],
  ['conversions-manager', 'api', 'v1', 'tracking-beacons'],
  ['conversions-manager', 'api', 'v1', 'tracking-beacons', 'event'],
  ['conversions-manager', 'api', 'v1', 'attribution', 'click'],
  ['conversions-manager', 'api', 'v1', 'attribution', 'event'],
  ['conversions-manager', 'api', 'v1', 'attribution', 'access-creds'],
  ['conversions-manager', 'api', 'v1', 'attribution', 'seeka-creds'],
  ['conversions-manager', 'api', 'v1', 'attribution', 'pending-click'],
  // Folded homepage surface (browser-visible `/home` mount). The BFF is served
  // publicly under `/home/api/**` and rewritten onto `/api/home/**`; both forms
  // are registered so a completion log normalizes to the same bounded template
  // whichever shape `req.url` carries when the handler runs. The `/home/_next/*`
  // namespace is monitor-relevant: a NEW cohort on it is a seam wiring bug.
  ['home'],
  ['home', 'it-lists'],
  ['home', '_next', 'static'],
  ['home', '_next', 'image'],
  ['home', 'api', 'rokt'],
  ['home', 'api', 'zendesk-menu'],
  ['home', 'api', 'events', 'saved-event-ids'],
  ['home', 'api', 'popular_events'],
  ['home', 'api', 'nearby_cities'],
  ['home', 'api', 'search'],
  ['home', 'api', 'search', 'recommendations'],
  ['home', 'api', 'guides'],
  ['home', 'api', 'guides', ':id'],
  ['home', 'api', 'user'],
  ['home', 'api', 'user', 'id'],
  ['home', 'api', 'user', 'subscription'],
  ['home', 'api', 'geo', 'place_from_request'],
  ['home', 'api', 'geo', 'place_from_google_place_id'],
  ['home', 'api', 'geo', 'place_from_coordinates'],
  ['home', 'api', 'v3'],
  ['api', 'home', 'rokt'],
  ['api', 'home', 'zendesk-menu'],
  ['api', 'home', 'events', 'saved-event-ids'],
  ['api', 'home', 'popular_events'],
  ['api', 'home', 'nearby_cities'],
  ['api', 'home', 'search'],
  ['api', 'home', 'search', 'recommendations'],
  ['api', 'home', 'guides'],
  ['api', 'home', 'guides', ':id'],
  ['api', 'home', 'user'],
  ['api', 'home', 'user', 'id'],
  ['api', 'home', 'user', 'subscription'],
  ['api', 'home', 'geo', 'place_from_request'],
  ['api', 'home', 'geo', 'place_from_google_place_id'],
  ['api', 'home', 'geo', 'place_from_coordinates'],
  ['api', 'home', 'v3'],

  // Folded listings surface. Two spellings reach the handlers: the
  // browser-visible basePath prefix (`/e/<slug>`, `/e/api/**`) and the
  // canonical rewrite target (`/listings/**` document mount, `/api/listings/**`
  // BFF). Both are registered so a completion log normalizes to the same
  // bounded template whichever shape `req.url` carries. `/e/_next/*` and
  // `/listings/_next/*` are monitor-relevant: a NEW cohort on them is a seam
  // wiring bug.
  ['e'],
  ['e', 'preview'],
  ['e', '_next', 'static'],
  ['e', '_next', 'image'],
  ['e', 'api'],
  ['e', 'api', 'eventview'],
  ['e', 'api', 'rokt'],
  ['e', 'api', 'user', 'subscription'],
  ['e', 'api', 'v3'],
  ['listings'],
  ['listings', ':id'],
  ['listings', 'preview'],
  ['listings', ':id', 'protected'],
  ['listings', '_next', 'static'],
  ['listings', '_next', 'image'],
  ['api', 'listings'],
  ['api', 'listings', 'eventview'],
  ['api', 'listings', 'rokt'],
  ['api', 'listings', 'user', 'subscription'],
  ['api', 'listings', 'v3'],
  // Waitlist organizer BFF: served under the settings page prefix (covered by
  // the `/waitlist-*` CloudFront seam) and reachable via the canonical rewrite
  // target; both shapes are registered so a completion log normalizes to the
  // same bounded template.
  ['waitlist-settings', 'api', 'events', ':id', 'waitlist'],
  ['waitlist-settings', 'api', 'events', ':id', 'waitlist-settings'],
  ['waitlist-settings', 'api', 'events', ':id', 'waitlist', 'add'],
  ['waitlist-settings', 'api', 'events', ':id', 'waitlist', 'release'],
  ['waitlist-settings', 'api', 'events', ':id', 'waitlist', 'entries', 'delete'],
  ['api', 'waitlist-settings', 'events', ':id', 'waitlist'],
  ['api', 'waitlist-settings', 'events', ':id', 'waitlist-settings'],
  ['api', 'waitlist-settings', 'events', ':id', 'waitlist', 'add'],
  ['api', 'waitlist-settings', 'events', ':id', 'waitlist', 'release'],
  ['api', 'waitlist-settings', 'events', ':id', 'waitlist', 'entries', 'delete'],
] as const;

function isSafeStaticSegment(segments: string[], index: number): boolean {
  return ROUTE_TEMPLATES.some((template) =>
    template[index] === segments[index]
    && template.slice(0, index).every((segment, prefixIndex) =>
      segment === ':id' || segment === segments[prefixIndex]),
  );
}

export function homepageDocumentPath(value: string): string {
  const pathname = value.split(/[?#]/, 1)[0] || '/';
  const matched = /^\/_next\/data\/[^/]+\/(home(?:\/.*)?)\.json$/.exec(pathname);
  return matched ? `/${matched[1]}` : pathname;
}

export function normalizeRouteTemplate(value: string): string {
  const pathname = homepageDocumentPath(value);
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  return `/${segments.map((segment, index) =>
    isSafeStaticSegment(segments, index) ? segment : ':id').join('/')}`;
}
